import type { SmartStreamStore } from "./SmartStreamStore";

const NEW_THREAD_KEY = "__new__";

type RegistryEntry = {
  store: SmartStreamStore<any, any>;
  refCount: number;
};

const registry = new Map<string, RegistryEntry>();
const nameMap = new Map<string, string>();

// Thread ids Langgraph has materialized (checkpoint created) during this page
// session. Module-level so it survives component remounts — HMR/Fast Refresh,
// error-boundary resets, conditional unmount/remount of the chat subtree — that
// wipe per-hook refs like `isMaterializedRef`. A reserved thread whose id lives
// here must NOT have that id re-injected on the next submit, or LG's create
// raises 409 "thread already exists". This is the remount-proof backstop; the
// per-hook ref is still the fast path within a single mount.
const materializedThreadIds = new Set<string>();

export function markThreadMaterialized(threadId: string): void {
  materializedThreadIds.add(threadId);
}

export function isThreadMaterialized(threadId: string): boolean {
  return materializedThreadIds.has(threadId);
}

export function getStoreKey(
  apiUrl: string,
  assistantId: string,
  threadId: string | undefined
): string {
  return `${apiUrl}::${assistantId}::${threadId ?? NEW_THREAD_KEY}`;
}

export function getOrCreateStore<S extends SmartStreamStore<any, any>>(
  key: string,
  factory: () => S
): S {
  if (!registry.has(key)) {
    registry.set(key, { store: factory(), refCount: 0 });
  }
  return registry.get(key)!.store as S;
}

export function acquireStore(key: string): void {
  const entry = registry.get(key);
  if (entry) {
    entry.refCount++;
  }
}

export function releaseStore(key: string): void {
  const entry = registry.get(key);
  if (!entry) return;

  entry.refCount--;

  if (entry.refCount <= 0) {
    registry.delete(key);
    // Clean up any name mappings pointing to this key
    for (const [name, mappedKey] of nameMap) {
      if (mappedKey === key) {
        nameMap.delete(name);
      }
    }
  }
}

export function rekeyStore(oldKey: string, newKey: string): void {
  if (oldKey === newKey) return;

  const entry = registry.get(oldKey);
  if (!entry) return;

  // Don't overwrite if the new key already exists
  if (registry.has(newKey)) return;

  registry.set(newKey, entry);
  registry.delete(oldKey);

  // Update name mappings
  for (const [name, mappedKey] of nameMap) {
    if (mappedKey === oldKey) {
      nameMap.set(name, newKey);
    }
  }
}

export function registerName(name: string, key: string): void {
  nameMap.set(name, key);
}

export function getStoreByName(
  name: string
): SmartStreamStore<any, any> | undefined {
  const key = nameMap.get(name);
  if (!key) return undefined;
  return registry.get(key)?.store;
}

export function getRegistrySize(): number {
  return registry.size;
}

export function clearRegistry(): void {
  registry.clear();
  nameMap.clear();
  materializedThreadIds.clear();
}
